/**
 * Sol newsletter popup.
 *
 * Opens once the visitor has scrolled part way down a page AND spent a few
 * seconds on it, so neither a landing nor a fast flick sets it off. It is a
 * non-modal panel: it never takes focus, never locks scroll, and the page
 * behind it stays usable.
 *
 * It stays shut when:
 *   - the visitor closed it within the last `data-snooze` days,
 *   - they already signed up here (or in the footer form),
 *   - it was shown earlier this session and left alone,
 *   - they arrived from one of our own emails (utm_medium=email),
 *   - the page is under two screens tall, or
 *   - the cookie banner, a drawer, a modal or a form field is in use; it
 *     checks again every couple of seconds.
 *
 * The form posts in place. When Shopify answers with a captcha, or anything
 * this script does not recognise, it falls back to a normal POST and reopens
 * on the reloaded page in the right state.
 */

const STORE_KEY = 'sol-newsletter';
const SEEN_KEY = 'sol-newsletter-seen';
const POSTING_KEY = 'sol-newsletter-posting';
const DAY = 86400000;

/** Storage can be absent or throw (private windows, blocked site data). */
const store = {
  get(area, key) {
    try {
      return window[area].getItem(key);
    } catch {
      return null;
    }
  },
  set(area, key, value) {
    try {
      window[area].setItem(key, value);
    } catch {}
  },
  remove(area, key) {
    try {
      window[area].removeItem(key);
    } catch {}
  },
};

function readState() {
  try {
    return JSON.parse(store.get('localStorage', STORE_KEY) || 'null');
  } catch {
    return null;
  }
}

function writeState(state) {
  store.set('localStorage', STORE_KEY, JSON.stringify({ state, at: Date.now() }));
}

class SolNewsletterPopup extends HTMLElement {
  #panel;
  #form;
  #startedAt = 0;
  #timer = 0;
  #closeTimer = 0;
  #returnFocus = null;
  #barObserver = null;

  connectedCallback() {
    this.#panel = this.querySelector('.sol-nl__panel');
    this.#form = this.querySelector('form');
    if (!this.#panel || !this.#form) return;

    this.querySelector('[data-sol-nl-close]')?.addEventListener('click', () => this.close(true));
    this.#form.addEventListener('submit', this.#onSubmit);
    this.#panel.addEventListener('focusin', this.#onFocusIn);
    document.addEventListener('keydown', this.#onKeydown);
    this.#watchStickyBar();

    if (this.hasAttribute('data-design-mode')) {
      document.addEventListener('shopify:section:select', this.#onEditorSelect);
      document.addEventListener('shopify:section:deselect', this.#onEditorDeselect);
      // A settings change re-renders the section; show the edit straight away.
      document.addEventListener('shopify:section:load', this.#onEditorSelect);
      return;
    }

    if (this.#resumeAfterPost()) return;
    if (!this.#eligible()) return;

    this.#startedAt = performance.now();
    // Scroll does not bubble, so listen in the capture phase: from 990px
    // Horizon scrolls .page-wrapper, not the window (base.css).
    document.addEventListener('scroll', this.#check, { passive: true, capture: true });
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.#check, { capture: true });
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('shopify:section:select', this.#onEditorSelect);
    document.removeEventListener('shopify:section:deselect', this.#onEditorDeselect);
    document.removeEventListener('shopify:section:load', this.#onEditorSelect);
    this.#barObserver?.disconnect();
    clearTimeout(this.#timer);
  }

  get isOpen() {
    return !this.#panel.hidden;
  }

  open() {
    clearTimeout(this.#closeTimer);
    if (this.isOpen) {
      this.classList.add('is-open');
      return;
    }
    document.removeEventListener('scroll', this.#check, { capture: true });
    clearTimeout(this.#timer);
    store.set('sessionStorage', SEEN_KEY, '1');

    this.#panel.hidden = false;
    // One frame in the closed position, so the slide-in has somewhere to start.
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
  }

  /** @param {boolean} byVisitor  true when the visitor dismissed it */
  close(byVisitor = false) {
    if (!this.isOpen) return;
    if (byVisitor && readState()?.state !== 'subscribed') writeState('closed');

    const hadFocus = this.#panel.contains(document.activeElement);
    this.classList.remove('is-open');

    const finish = () => {
      this.#panel.hidden = true;
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
    else this.#closeTimer = window.setTimeout(finish, 340);

    if (hadFocus) {
      if (this.#returnFocus?.isConnected) this.#returnFocus.focus();
      else /** @type {HTMLElement} */ (document.activeElement)?.blur();
    }
  }

  /* --- When to open ------------------------------------------------------ */

  #eligible() {
    const saved = readState();
    if (saved?.state === 'subscribed') return false;
    if (saved?.state === 'closed' && Date.now() - saved.at < Number(this.dataset.snooze || 30) * DAY) return false;
    if (store.get('sessionStorage', SEEN_KEY)) return false;

    const params = new URLSearchParams(window.location.search);
    if (params.get('utm_medium') === 'email') return false;
    if (params.get('customer_posted') === 'true') {
      // The footer form posted successfully; they're already on the list.
      writeState('subscribed');
      return false;
    }
    return true;
  }

  #check = () => {
    const { top, range, view } = this.#scrollPosition();
    // Short pages (contact, shipping) are a task, not a read; leave them alone.
    if (range < view) return;
    if (top / range < Number(this.dataset.scroll || 40) / 100) return;

    const remaining = Number(this.dataset.delay || 0) * 1000 - (performance.now() - this.#startedAt);
    if (remaining > 0) {
      clearTimeout(this.#timer);
      this.#timer = window.setTimeout(this.#check, remaining);
      return;
    }

    if (this.#busy()) {
      // Try again shortly, so it follows the consent banner or a closed drawer
      // without waiting for another scroll.
      clearTimeout(this.#timer);
      this.#timer = window.setTimeout(this.#check, 2000);
      return;
    }
    this.open();
  };

  /** The page wrapper scrolls on desktop, the document below 990px. */
  #scrollPosition() {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1) {
      return { top: wrapper.scrollTop, range: wrapper.scrollHeight - wrapper.clientHeight, view: wrapper.clientHeight };
    }
    const root = document.documentElement;
    return { top: window.scrollY, range: root.scrollHeight - window.innerHeight, view: window.innerHeight };
  }

  /** Something else has the visitor's attention: cookie consent, a drawer, a modal or a field. */
  #busy() {
    const consent = document.querySelector('#shopify-pc__banner, #shopify-pc__prefs');
    if (consent?.checkVisibility?.() ?? consent?.getClientRects().length) return true;
    if (document.querySelector('dialog[open], details.menu-drawer-container[open]')) return true;
    const active = document.activeElement;
    return Boolean(active?.matches('input, textarea, select, [contenteditable="true"]'));
  }

  /* --- Product pages: stand above the sticky add-to-cart bar ------------- */

  #watchStickyBar() {
    const bar = document.querySelector('.sticky-add-to-cart__bar');
    if (!bar) return;

    const update = () => {
      const lift = bar.dataset.stuck === 'true' ? bar.offsetHeight : 0;
      this.style.setProperty('--sol-nl-lift', `${lift}px`);
    };
    this.#barObserver = new MutationObserver(update);
    this.#barObserver.observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
    update();
  }

  /* --- Form -------------------------------------------------------------- */

  #onSubmit = async (event) => {
    event.preventDefault();
    const button = this.#form.querySelector('[type="submit"]');
    button?.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;
    this.#showError('');

    try {
      const response = await fetch(this.#form.action, {
        method: 'POST',
        body: new FormData(this.#form),
        headers: { Accept: 'text/html' },
      });

      if (response.url.includes('customer_posted=true')) return this.#done();
      if (response.url.includes('/challenge')) return this.#postNatively();

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const message = doc.querySelector('[data-sol-nl-error]:not([hidden])')?.textContent.trim();
      if (message) return this.#showError(message);

      this.#postNatively();
    } catch {
      this.#postNatively();
    } finally {
      button?.removeAttribute('aria-busy');
      if (button) button.disabled = false;
    }
  };

  /** A normal POST, so Shopify can show its captcha. We pick up on return. */
  #postNatively() {
    store.set('sessionStorage', POSTING_KEY, '1');
    HTMLFormElement.prototype.submit.call(this.#form);
  }

  /** Back from a normal POST that this popup started: reopen in its result. */
  #resumeAfterPost() {
    if (!store.get('sessionStorage', POSTING_KEY)) return false;
    store.remove('sessionStorage', POSTING_KEY);

    const posted = new URLSearchParams(window.location.search).get('customer_posted') === 'true';
    const error = this.querySelector('[data-sol-nl-error]:not([hidden])');
    if (!posted && !error) return false;

    this.open();
    if (posted) this.#done();
    return true;
  }

  #showError(message) {
    const error = this.querySelector('[data-sol-nl-error]');
    const input = this.#form.querySelector('input[type="email"]');
    if (!error || !input) return;
    error.textContent = message;
    error.hidden = !message;
    input.toggleAttribute('aria-invalid', Boolean(message));
    if (message) input.focus();
  }

  #done() {
    writeState('subscribed');
    const signup = this.querySelector('[data-sol-nl-signup]');
    const done = this.querySelector('[data-sol-nl-done]');
    if (!signup || !done) return;
    signup.hidden = true;
    done.hidden = false;
    this.#panel.setAttribute('aria-labelledby', `${this.#panel.id}-done`);
    this.#panel.setAttribute('aria-describedby', `${this.#panel.id}-done-lead`);
    /** @type {HTMLElement | null} */ (done.querySelector('[data-sol-nl-done-heading]'))?.focus();
  }

  /* --- Small handlers ---------------------------------------------------- */

  #onFocusIn = (event) => {
    if (event.relatedTarget instanceof HTMLElement && !this.#panel.contains(event.relatedTarget)) {
      this.#returnFocus = event.relatedTarget;
    }
  };

  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    // Leave Escape to a modal that is open over us.
    if (document.querySelector('dialog[open]')) return;
    this.close(true);
  };

  #onEditorSelect = (event) => {
    if (event.detail?.sectionId === this.dataset.sectionId) this.open();
  };

  #onEditorDeselect = (event) => {
    if (event.detail?.sectionId === this.dataset.sectionId) this.close();
  };
}

if (!customElements.get('sol-newsletter-popup')) {
  customElements.define('sol-newsletter-popup', SolNewsletterPopup);
}
